import React, { useState } from 'react';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CryingEmoji = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const cryingEmojis = {
    loudCry: '😭',
    cry: '😢',
    sob: '😪',
    sadFace: '😥',
    weary: '😩',
    persevere: '😣',
    disappointed: '😞',
    worried: '😟',
    anxious: '😰',
    catCry: '😿'
  };

  const handleCopy = (emoji: string) => {
    navigator.clipboard.writeText(emoji);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What does the crying emoji mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The crying emoji 😭 represents intense emotions of sadness, grief, or overwhelming feelings. It can also be used to express extreme happiness or laughter (crying tears of joy)."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between 😭 and 😢?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The loudly crying face 😭 represents more intense emotions than the crying face 😢. The 😭 emoji shows streams of tears and is used for extreme sadness or overwhelming emotions, while 😢 shows a single tear and represents general sadness or disappointment."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Crying Emoji 😭 Complete Guide & Meanings [{currentYear}] 😢</title>
        <meta name="description" content={`【COMPLETE GUIDE】 Learn all about crying emojis 😭 Meanings, usage & examples 😢 Updated for ${currentYear} ✨ Copy & paste all crying face emojis 💭 Perfect for expressing emotions!`} />
        <link rel="canonical" href="https://emojicollection.net/emoji/crying" />
        
        <meta property="og:title" content={`Crying Emoji 😭 Complete Guide & Meanings [${currentYear}] 😢`} />
        <meta property="og:description" content={`Learn all about crying emojis 😭 Meanings, usage & examples 😢 Updated for ${currentYear} ✨ Copy & paste all crying face emojis!`} />
        <meta property="og:url" content="https://emojicollection.net/emoji/crying" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1517837543704-3617dcc38986?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Crying Emoji 😭 Complete Guide & Meanings [${currentYear}] 😢`} />
        <meta name="twitter:description" content={`Learn all about crying emojis 😭 Meanings, usage & examples 😢 Updated for ${currentYear} ✨ Copy & paste all crying face emojis!`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1517837543704-3617dcc38986?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="crying emoji, sad emoji, crying face emoji, tears emoji, emotional emoji, sad face emoji, crying emoji meaning, crying emoji copy paste" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1517837543704-3617dcc38986?auto=format&fit=crop&w=1200&q=80"
                alt="Emotional expression through emojis"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Crying Emoji 😭 Complete Guide & Meanings [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Discover everything about crying emojis - from their meanings and usage to platform differences. Updated for {currentYear} with the latest designs and examples.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">All Crying Emojis</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(cryingEmojis).map(([key, emoji]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-center mb-2">
                      <span className="text-4xl">{emoji}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(emoji)}
                      className={`w-full inline-flex items-center justify-center px-3 py-2 rounded text-sm font-medium ${
                        copied ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'
                      } transition-colors`}
                    >
                      {copied ? (
                        <>
                          <Check className="mr-1" size={14} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-1" size={14} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="prose prose-lg max-w-none">
              <h2>Meaning & Usage</h2>
              <p>
                The crying emoji comes in several variations, each expressing different levels of sadness or emotional intensity:
              </p>
              <ul>
                <li><strong>Loudly Crying Face 😭</strong> - Intense emotions, extreme sadness, or overwhelming feelings</li>
                <li><strong>Crying Face 😢</strong> - General sadness, disappointment, or sympathy</li>
                <li><strong>Sleepy Face 😪</strong> - Tiredness, exhaustion, or feeling drained</li>
                <li><strong>Sad but Relieved Face 😥</strong> - Mixed emotions, bittersweet moments</li>
                <li><strong>Weary Face 😩</strong> - Exhaustion, frustration, or being fed up</li>
              </ul>

              <h2>Platform Differences</h2>
              <p>
                Crying emojis may appear slightly different across platforms:
              </p>
              <ul>
                <li><strong>Apple:</strong> More realistic tears with gradient effects</li>
                <li><strong>Google:</strong> Flatter design with simplified tears</li>
                <li><strong>Samsung:</strong> Glossy finish with dimensional tears</li>
                <li><strong>Microsoft:</strong> Minimalist design with basic tears</li>
              </ul>

              <h2>Common Uses</h2>
              <p>
                Crying emojis are versatile and can be used in various contexts:
              </p>
              <ul>
                <li>Expressing genuine sadness or grief</li>
                <li>Showing empathy or sympathy</li>
                <li>Indicating overwhelming emotions (both positive and negative)</li>
                <li>Dramatic emphasis in casual conversation</li>
                <li>Expressing extreme happiness or laughter (😭 specifically)</li>
              </ul>

              <h2>Cultural Impact</h2>
              <p>
                The crying emoji has become a significant part of digital communication:
              </p>
              <ul>
                <li>Universal symbol of emotional expression</li>
                <li>Popular in meme culture and social media</li>
                <li>Used across age groups and cultures</li>
                <li>Effective for emotional storytelling</li>
              </ul>

              <h2>Best Practices</h2>
              <p>
                To use crying emojis effectively:
              </p>
              <ul>
                <li>Choose the appropriate intensity for the situation</li>
                <li>Consider the context and relationship with the recipient</li>
                <li>Combine with text for clearer communication</li>
                <li>Use multiple emojis for emphasis when appropriate</li>
              </ul>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What does the crying emoji mean?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    The crying emoji 😭 represents intense emotions of sadness, grief, or overwhelming feelings. It can also be used to express extreme happiness or laughter (crying tears of joy).
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What's the difference between 😭 and 😢?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    The loudly crying face 😭 represents more intense emotions than the crying face 😢. The 😭 emoji shows streams of tears and is used for extreme sadness or overwhelming emotions, while 😢 shows a single tear and represents general sadness or disappointment.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Can crying emojis be used for positive emotions?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Yes! Particularly the loudly crying face 😭 is often used to express overwhelming positive emotions, like extreme happiness, uncontrollable laughter, or being deeply moved by something beautiful or touching.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default CryingEmoji;